import { format, parse } from 'date-fns';
import { formatInTimeZone, fromZonedTime, getTimezoneOffset } from 'date-fns-tz';
import { DateTime } from 'luxon';
import { getCurrentUser, getTimeZone } from '../services/helper';
import { hhmmToAmPm } from './dateHelpers';

const timeZone = getTimeZone(); // current user's timezone

// given a UTC time, transform it to current time zone with given format
export const formatDate = (date: Date, format: string = 'MM/dd/yyyy') => {
  // Format the zoned time in desired format
  try {
    return formatInTimeZone(date, timeZone, format);
  } catch (e) {
    return 'Invalid Date';
  }
};

export const formatUTCDateOnly = (date: Date, format: string = 'yyyy-MM-dd') => {
  return formatInTimeZone(date, 'UTC', format);
};

export const getCurrentDateOnly = () => {
  return format(new Date(), 'yyyy-MM-dd');
};

export const getDaysOffsetDateOnly = (offsetDays: number) => {
  let d = new Date();
  d.setDate(d.getDate() + offsetDays);
  return format(d, 'yyyy-MM-dd');
};

// given a date in a current time zone, transform it to UTC with given format
export const dateToUTC = (date: Date, format: string = 'yyyy-MM-dd') => {
  // Format the zoned time in desired format
  return formatInTimeZone(date, 'UTC', format);
};

// gives current date using current time zone
export const dateInTimeZone = (date: Date = new Date()) => {
  return fromZonedTime(date, timeZone); // convers time to utc
};

// creates date with current time zone using a string
export const dateFromString = (date: string, format: string = 'yyyy-MM-dd') => {
  // Parse the date string into a Date object
  return fromZonedTime(parse(date, format, new Date()), timeZone);
};

export const UTCToDate = (date: string, format: string = 'yyyy-MM-dd') => {
  const UTCDate = parse(date ?? '', format, new Date());
  const utcDateCorrected = new Date(UTCDate.getTime() - UTCDate.getTimezoneOffset() * 60000);
  return utcDateCorrected;
};

export const UTCTimeToLocal = (time?: string, format: string = 'h:mma') => {
  const regex = /(\d{1,2}):(\d{2})\s?([ap]+m)/i;
  const match = time?.match(regex);

  if (match) {
    const hours = parseInt(match[1], 10);
    const minutes = parseInt(match[2], 10); // Extract minutes (e.g., 30)
    const amPm = match[3].toLowerCase(); // Extract "am" or "pm"

    // Convert hours to 24-hour format
    let hours24 = hours;
    if (amPm === 'pm' && hours24 < 12) {
      hours24 += 12;
    } else if (amPm === 'am' && hours24 === 12) {
      hours24 = 0;
    }

    const now = new Date();
    const dateInUTC = new Date(
      Date.UTC(now.getFullYear(), now.getMonth(), now.getDate(), hours24, minutes)
    );
    // Convert the time from UTC to the local timezone
    const localTime = new Date(dateInUTC.toLocaleString('en-US', { timeZone: timeZone }));
    // Format the result back to 'h:mma' format
    const formattedTime = localTime
      .toLocaleTimeString([], { hour: 'numeric', minute: 'numeric', hour12: true })
      .toLowerCase();
    return formattedTime.replace('am', 'AM').replace('pm', 'PM');
  }
};

export const getStartAndEndDateTime = (date: string, formatted_time: string) => {
  const currentUser = getCurrentUser();

  if (!date || !formatted_time) return '';
  const [startStr, endStr] = formatted_time.split(' - ').map((s) => s.trim().toLowerCase());

  const starts_at_utc = DateTime.fromFormat(`${date} ${startStr}`, 'yyyy-MM-dd h:mma', {
    zone: 'utc'
  });
  const ends_at_utc = DateTime.fromFormat(`${date} ${endStr}`, 'yyyy-MM-dd h:mma', { zone: 'utc' });

  const starts_at_local = starts_at_utc.setZone(currentUser?.timezone ?? 'America/Denver');
  const ends_at_local = ends_at_utc.setZone(currentUser?.timezone ?? 'America/Denver');

  const formatted = `${starts_at_local.toFormat('h:mma').toLowerCase()} - ${ends_at_local.toFormat('h:mma').toLowerCase()}`;

  return formatted;
};

export const getDateInUserTimeZone = (date: string, format: string = 'yyyy-MM-dd') => {
  const currentUser = getCurrentUser();
  if (!currentUser?.timezone) return date;

  const dateInUTC = DateTime.fromFormat(`${date}`, 'yyyy-MM-dd', { zone: 'utc' });
  const dateInTimeZone = dateInUTC.setZone(currentUser.timezone);
  return dateInTimeZone.toFormat(format);
};

/**
 * Stable two-letter abbreviation for each supported location timezone.
 * v3 deliberately does NOT use DST-aware abbreviations (e.g. MDT vs MST) per
 * Sam's spec — parents see the same letters year-round regardless of season.
 */
const TZ_ABBREVIATION: Record<string, string> = {
  'US/Pacific': 'PT',
  'America/Los_Angeles': 'PT',
  'US/Mountain': 'MT',
  'America/Denver': 'MT',
  'US/Central': 'CT',
  'America/Chicago': 'CT',
  'US/Eastern': 'ET',
  'America/New_York': 'ET',
  'US/Hawaii': 'HT',
  'Pacific/Honolulu': 'HT',
  'US/Alaska': 'AKT',
  'America/Anchorage': 'AKT',
};

/**
 * Does a given IANA timezone observe DST?
 * Compares January vs July offsets.
 */
const zoneObservesDST = (tz: string): boolean => {
  if (!tz) return false;
  const year = new Date().getUTCFullYear();
  const jan = new Date(Date.UTC(year, 0, 15, 12, 0, 0));
  const jul = new Date(Date.UTC(year, 6, 15, 12, 0, 0));
  return getTimezoneOffset(tz, jan) !== getTimezoneOffset(tz, jul);
};

/**
 * Is the given date currently in DST for the given timezone?
 * Returns false for never-DST zones like Arizona.
 */
const isInDST = (date: Date, tz: string): boolean => {
  if (!zoneObservesDST(tz)) return false;
  const year = date.getUTCFullYear();
  const jul = new Date(Date.UTC(year, 6, 15, 12, 0, 0));
  return getTimezoneOffset(tz, date) === getTimezoneOffset(tz, jul);
};

/**
 * Get the two-letter abbreviation for a given location TZ.
 *
 * Phoenix (US/Arizona) is special: never observes DST so its UTC-7 offset
 * aligns with Mountain Standard in winter and Pacific Daylight in summer.
 * Display "MT" in winter and "PT" in summer to match whichever neighboring
 * zone Phoenix happens to align with at the moment.
 */
export const getTimezoneAbbreviation = (locationTimezone: string): string => {
  if (locationTimezone === 'US/Arizona' || locationTimezone === 'America/Phoenix') {
    // Sam's spec: show MT during DST (matches Mountain Daylight is wrong; matches
    // Mountain when MT zones are in DST and aligned to Arizona's UTC-7), PT otherwise.
    // We check US/Mountain DST: in DST → MT. Out of DST → PT.
    const now = new Date();
    return isInDST(now, 'US/Mountain') ? 'MT' : 'PT';
  }
  return TZ_ABBREVIATION[locationTimezone] ?? '';
};

/**
 * Normalize various time-of-day input formats to "HH:mm:ss".
 *
 * The API returns starts_at/ends_at as either "21:35:00" (raw TIME column) or
 * "9:35 PM" (after the SQL DATE_FORMAT transform). Both should produce the
 * same normalized form.
 */
const toHHmmss = (input: string): string | null => {
  if (!input) return null;
  if (/^\d{2}:\d{2}:\d{2}$/.test(input)) return input;
  const hhmm = input.trim().match(/^(\d{1,2}):(\d{2})$/);
  if (hhmm && !/\s*(AM|PM|am|pm)$/i.test(input.trim())) {
    return `${String(parseInt(hhmm[1], 10)).padStart(2, '0')}:${hhmm[2]}:00`;
  }
  const ampm = input.trim().match(/^(\d{1,2}):(\d{2})\s*(AM|PM|am|pm)$/);
  if (ampm) {
    let h = parseInt(ampm[1], 10);
    const m = ampm[2];
    const period = ampm[3].toUpperCase();
    if (period === 'PM' && h < 12) h += 12;
    if (period === 'AM' && h === 12) h = 0;
    return `${String(h).padStart(2, '0')}:${m}:00`;
  }
  return null;
};

/**
 * Display a session time in its location's timezone, replicating v2 Rails'
 * Zone::FancyTime(ignore_dst: true) convention.
 *
 * v2 stored session times so that subtracting an hour during DST + applying
 * standard offset gives the actual class wall-clock time. v3 inherits this
 * data convention and needs to reproduce the same math on display.
 *
 * Uses LOCATION TZ (not viewer TZ) so the same class shows the same number
 * to every viewer regardless of where they are.
 *
 * @param utcTimeOfDay - stored starts_at or ends_at value
 * @param locationTimezone - IANA TZ name (e.g. "US/Mountain")
 * @returns formatted string like "1:45 PM MT"
 *
 * Examples:
 *   displaySessionTime("20:45:00", "US/Mountain") → "1:45 PM MT"
 *   displaySessionTime("21:05:00", "US/Central")  → "2:05 PM CT"
 *   displaySessionTime("22:45:00", "US/Arizona")  → "3:45 PM MT" (winter)
 *                                                   "3:45 PM PT" (summer)
 */
export const displaySessionTime = (
    utcTimeOfDay: string | null | undefined,
    locationTimezone: string | null | undefined,
): string => {
  if (!utcTimeOfDay) return '';
  if (!locationTimezone) return utcTimeOfDay;

  const hms = toHHmmss(utcTimeOfDay);
  if (!hms) return utcTimeOfDay;

  // Build a UTC moment using today's date plus the stored time-of-day.
  const today = new Date();
  const isoDate = today.toISOString().slice(0, 10);
  let utcMoment = new Date(`${isoDate}T${hms}Z`);

  // FancyTime ignore_dst: if location TZ is in DST today, subtract 1 hour.
  // Net effect: displayed wall-clock is the same number regardless of season.
  if (isInDST(utcMoment, locationTimezone)) {
    utcMoment = new Date(utcMoment.getTime() - 60 * 60 * 1000);
  }

  const timeStr = formatInTimeZone(utcMoment, locationTimezone, 'h:mm a');
  const abbrev = getTimezoneAbbreviation(locationTimezone);

  return abbrev ? `${timeStr} ${abbrev}` : timeStr;
};

/**
 * Format both start and end times. Common case in display surfaces.
 * Deduplicates the abbreviation so we get "1:45 PM - 3:00 PM MT" instead of
 * "1:45 PM MT - 3:00 PM MT".
 */
export const displaySessionTimeRange = (
    startUtc: string | null | undefined,
    endUtc: string | null | undefined,
    locationTimezone: string | null | undefined,
): string => {
  const start = displaySessionTime(startUtc, locationTimezone);
  const end = displaySessionTime(endUtc, locationTimezone);
  if (!start && !end) return '';
  if (!end) return start;
  if (!start) return end;
  const abbrev = getTimezoneAbbreviation(locationTimezone || '');
  if (abbrev && start.endsWith(` ${abbrev}`) && end.endsWith(` ${abbrev}`)) {
    const startNoAbbrev = start.slice(0, -(abbrev.length + 1));
    return `${startNoAbbrev} - ${end}`;
  }
  return `${start} - ${end}`;
};

/**
 * Convert a stored session time to venue-local HH:mm for HTML time inputs.
 * Mirrors displaySessionTime / FancyTime ignore_dst convention.
 */
export const sessionTimeToFormHhmm = (
    storedTimeOfDay: string | null | undefined,
    locationTimezone: string | null | undefined,
): string => {
  if (!storedTimeOfDay) return '';
  if (!locationTimezone) {
    const hms = toHHmmss(storedTimeOfDay);
    return hms ? hms.slice(0, 5) : '';
  }

  const hms = toHHmmss(storedTimeOfDay);
  if (!hms) return '';

  const today = new Date();
  const isoDate = today.toISOString().slice(0, 10);
  let utcMoment = new Date(`${isoDate}T${hms}Z`);

  if (isInDST(utcMoment, locationTimezone)) {
    utcMoment = new Date(utcMoment.getTime() - 60 * 60 * 1000);
  }

  return formatInTimeZone(utcMoment, locationTimezone, 'HH:mm');
};

/**
 * Inverse of sessionTimeToFormHhmm — venue-local HH:mm back to stored UTC wall-clock.
 */
export const formHhmmToStoredSessionTime = (
    formHhmm: string,
    locationTimezone: string | null | undefined,
): string => {
  if (!formHhmm) return '';
  if (!locationTimezone) return `${formHhmm}:00`;

  const today = new Date();
  const isoDate = today.toISOString().slice(0, 10);
  const zonedLocal = parse(`${isoDate} ${formHhmm}`, 'yyyy-MM-dd HH:mm', new Date());
  let utcMoment = fromZonedTime(zonedLocal, locationTimezone);

  if (isInDST(utcMoment, locationTimezone)) {
    utcMoment = new Date(utcMoment.getTime() + 60 * 60 * 1000);
  }

  return formatInTimeZone(utcMoment, 'UTC', 'HH:mm:ss');
};

/** Venue-local form HH:mm → AM/PM string expected by the course/lesson API setters. */
export const formHhmmToStoredSessionAmPm = (
    formHhmm: string,
    locationTimezone: string | null | undefined,
): string => {
  const stored = formHhmmToStoredSessionTime(formHhmm, locationTimezone);
  if (!stored) return '';
  return hhmmToAmPm(stored.slice(0, 5));
};

/**
 * Stored FancyTime-encoded time-of-day + calendar date -> true instant.
 *
 * Stored times are wall-clock plus the location's STANDARD UTC offset (v2's
 * Zone::FancyTime ignore_dst convention). During DST the encoded value runs an
 * hour ahead of the true instant, and the encoder wraps modulo 24h while the
 * date column does not - a 4pm Pacific time stores as 00:00:00 - so we decode to
 * a wall-clock time and re-anchor it on dateOn rather than reading the stored
 * value as UTC directly.
 *
 * DST is evaluated against dateOn, not today, so future-dated windows across a
 * transition resolve correctly.
 *
 * An absent time means "no time given" and resolves to midnight in the
 * location's timezone.
 *
 * Mirror of sfcStoredDateTimeToInstant in
 * sticky-fingers-api-3/src/utils/timeConversion.ts. Keep the two in sync.
 */
export const storedSessionDateTimeToInstant = (
    dateOn: string | null | undefined,
    storedTimeOfDay: string | null | undefined,
    locationTimezone: string | null | undefined,
): Date | null => {
  if (!dateOn) return null;
  const dateOnly = dateOn.includes('T') ? dateOn.split('T')[0] : dateOn;

  if (!storedTimeOfDay?.trim()) {
    return locationTimezone
        ? fromZonedTime(`${dateOnly}T00:00:00`, locationTimezone)
        : new Date(`${dateOnly}T00:00:00Z`);
  }

  const hms = toHHmmss(storedTimeOfDay);
  if (!hms) return null;

  let naive = new Date(`${dateOnly}T${hms}Z`);
  if (Number.isNaN(naive.getTime())) return null;
  if (!locationTimezone) return naive;

  if (isInDST(naive, locationTimezone)) {
    naive = new Date(naive.getTime() - 60 * 60 * 1000);
  }

  const wallClock = formatInTimeZone(naive, locationTimezone, 'HH:mm:ss');
  return fromZonedTime(`${dateOnly}T${wallClock}`, locationTimezone);
};

/**
 * Same display as displaySessionTimeRange, but for list rows that only carry a
 * server-formatted UTC wall-clock string ("7:45pm - 8:45pm") plus a date, not
 * starts_at/ends_at. Rebuilds the UTC instants and delegates so the location-tz,
 * DST-ignore convention, and two-letter abbreviation are applied consistently.
 */
export const displaySessionTimeRangeFromFormatted = (
    formattedTime: string | null | undefined,
    locationTimezone: string | null | undefined,
): string => {
  if (!formattedTime) return '';
  const [startStr, endStr] = formattedTime.split(' - ').map((s) => s.trim().toLowerCase());
  // Date is irrelevant: displaySessionTime applies the zone's standard (DST-ignored)
  // offset, so any valid day yields the same wall-clock. List rows often have a null
  // starts_on, so we anchor on a fixed standard-time date.
  const startUtc = DateTime.fromFormat(`2000-01-15 ${startStr}`, 'yyyy-MM-dd h:mma', { zone: 'utc' });
  const endUtc = DateTime.fromFormat(`2000-01-15 ${endStr}`, 'yyyy-MM-dd h:mma', { zone: 'utc' });
  if (!startUtc.isValid || !endUtc.isValid) return '';
  return displaySessionTimeRange(
      startUtc.toFormat('HH:mm:ss'),
      endUtc.toFormat('HH:mm:ss'),
      locationTimezone
  );
};

/** Venue-local session time for cards and detail surfaces. */
export const getCourseSessionTimeDisplay = (course: {
  starts_at?: string | null;
  ends_at?: string | null;
  formatted_time?: string | null;
  venue?: { location?: { timezone?: string | null } | null } | null;
}): string => {
  const tz = course.venue?.location?.timezone ?? undefined;
  const fromRaw = displaySessionTimeRange(course.starts_at, course.ends_at, tz);
  if (fromRaw) return fromRaw;
  return displaySessionTimeRangeFromFormatted(course.formatted_time, tz);
};

/** True when a lesson has no explicit time override (null or legacy midnight placeholder). */
export const isMissingSessionTime = (time?: string | null): boolean => {
  if (!time) return true;
  const hms = toHHmmss(time);
  return !hms || hms === '00:00:00';
};

/** Venue-local time for an individual class, falling back to session defaults. */
export const getLessonSessionTimeDisplay = (
    lesson: {
      starts_at?: string | null;
      ends_at?: string | null;
      formatted_time?: string | null;
    },
    course: Parameters<typeof getCourseSessionTimeDisplay>[0],
): string => {
  const tz = course.venue?.location?.timezone ?? undefined;
  const startsAt = isMissingSessionTime(lesson.starts_at) ? course.starts_at : lesson.starts_at;
  const endsAt = isMissingSessionTime(lesson.ends_at) ? course.ends_at : lesson.ends_at;
  const fromRaw = displaySessionTimeRange(startsAt, endsAt, tz);
  if (fromRaw) return fromRaw;

  const formattedTime =
      isMissingSessionTime(lesson.starts_at) && isMissingSessionTime(lesson.ends_at)
          ? course.formatted_time
          : lesson.formatted_time;
  return displaySessionTimeRangeFromFormatted(formattedTime, tz);
};